import React from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { useAccountId } from '../../../../../redux/slices/appInfo/hooks';
import { CatalogSongScreen } from './CatalogSongScreen';
import { catalogSongPath, parseSongPageSection } from './catalogSongPath';

/**
 * The song page route: `/app/catalog/song/:catalogItemUuid` (design song-page a0). Every call is
 * account-scoped; the key starts a new page for each artist.
 */
const CatalogSongViewComponent: React.FC = () => {
  const accountId = useAccountId();
  const { catalogItemUuid } = useParams<{ catalogItemUuid: string }>();

  if (accountId === null || catalogItemUuid === undefined) {
    return <LoadingView />;
  }

  return (
    <CatalogSongScreen
      key={`${accountId}:${catalogItemUuid}`}
      accountId={accountId}
      catalogItemUuid={catalogItemUuid}
    />
  );
};

export const CatalogSongView = TypedMemo(CatalogSongViewComponent);

/**
 * The old lyric editor page (`/app/catalog/catalogLyrics?catalogItemUuid=<uuid>&accountId=<id>`,
 * design release-kit s8). Shipped native apps and old links open it: it goes to the Lyrics
 * section of the song page. A link with no row goes to the Catalog.
 */
const CatalogLyricsRedirectComponent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const catalogItemUuid = searchParams.get('catalogItemUuid') ?? '';
  const accountId = Number(searchParams.get('accountId'));
  const target =
    catalogItemUuid === ''
      ? '/catalog'
      : catalogSongPath(catalogItemUuid, {
          section: parseSongPageSection(searchParams.get('tab')) ?? 'lyrics',
          accountId: Number.isInteger(accountId) && accountId > 0 ? accountId : undefined,
        });
  return <Navigate to={target} replace />;
};

export const CatalogLyricsRedirect = TypedMemo(CatalogLyricsRedirectComponent);
